"use client";

import { useEffect, useState } from "react";
import { fetchLibraryTemplates, createLibraryTemplate, deleteLibraryTemplate, reorderLibraryTemplates, type ScenarioTemplateWithPartners } from "@/lib/queries";
import { formatPercent } from "@/lib/format";

// Body-only (no Header/page wrapper) so it composes into the Settings
// sidebar shell. Logic moved here unchanged from the old standalone
// /scenario-templates page -- that route now just renders this same
// component, see app/scenario-templates/page.tsx.
export function TemplatesSection() {
  const [templates, setTemplates] = useState<ScenarioTemplateWithPartners[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);
  // Native HTML5 drag-and-drop -- no dependency needed for a single
  // reorderable list. dragIndex tracks which row is being dragged;
  // dropIndex highlights the row it would land before/after.
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const load = () => {
    fetchLibraryTemplates()
      .then(setTemplates)
      .catch((e) => setError(e.message ?? String(e)));
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (t: ScenarioTemplateWithPartners) => {
    if (!window.confirm(`Delete "${t.name}"? This removes it from every offer it applies to. This cannot be undone.`)) {
      return;
    }
    setDeletingId(t.id);
    setError(null);
    try {
      await deleteLibraryTemplate(t.id);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeletingId(null);
    }
  };

  const handleDrop = async (targetIndex: number) => {
    if (!templates || dragIndex === null || dragIndex === targetIndex) {
      setDragIndex(null);
      return;
    }
    const reordered = [...templates];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(targetIndex, 0, moved);

    // Optimistic: update local order immediately, persist in the
    // background. Reverts (via load()) if the save fails.
    setTemplates(reordered);
    setDragIndex(null);
    setReordering(true);
    setError(null);
    try {
      await reorderLibraryTemplates(reordered.map((t) => t.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      load();
    } finally {
      setReordering(false);
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">Scenario Templates</h1>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
            Reusable partner-split templates, available to apply to any offer. To customize a
            template for one specific deal without changing the template itself, use &ldquo;Customize
            for this offer&rdquo; from a listing&apos;s detail page instead.
          </p>
        </div>
        <button
          onClick={() => setShowCreate((s) => !s)}
          className="shrink-0 rounded-md border px-3 py-1.5 text-sm font-medium"
          style={{ borderColor: "var(--color-accent)", color: "var(--color-accent-strong)" }}
        >
          {showCreate ? "Cancel" : "+ New Template"}
        </button>
      </div>

      {error && (
        <div
          className="mb-4 rounded-md border px-4 py-3 text-sm"
          style={{ borderColor: "var(--color-negative)", backgroundColor: "var(--color-negative-muted)", color: "var(--color-negative)" }}
        >
          {error}
        </div>
      )}

      {showCreate && (
        <div className="mb-6">
          <CreateTemplateForm
            onCreated={() => {
              setShowCreate(false);
              load();
            }}
          />
        </div>
      )}

      {!templates && !error && (
        <div className="py-16 text-center text-sm text-[var(--color-text-secondary)]">Loading…</div>
      )}

      {templates && templates.length === 0 && !showCreate && (
        <div className="py-16 text-center text-sm text-[var(--color-text-secondary)]">
          No templates yet — create one to get started.
        </div>
      )}

      {templates && templates.length > 0 && (
        <div className="space-y-3">
          {templates.map((t, index) => (
            <div
              key={t.id}
              draggable
              onDragStart={() => setDragIndex(index)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(index)}
              className="flex items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-1)] p-4"
              style={{ opacity: dragIndex === index ? 0.5 : 1, cursor: "grab" }}
            >
              <span className="select-none text-[var(--color-text-tertiary)]" aria-hidden="true">
                ⠿
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-[var(--color-text-primary)]">{t.name}</h3>
                  <button
                    onClick={() => handleDelete(t)}
                    disabled={deletingId === t.id}
                    className="rounded-md border px-2 py-0.5 text-xs font-medium disabled:opacity-50"
                    style={{ borderColor: "var(--color-negative)", color: "var(--color-negative)" }}
                  >
                    {deletingId === t.id ? "Deleting…" : "Delete"}
                  </button>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-[var(--color-text-secondary)]">
                  {t.partners.map((p) => (
                    <span key={p.id}>
                      {p.partner_label}: {formatPercent(p.ownership_pct, 0)}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
          {reordering && <div className="text-xs text-[var(--color-text-tertiary)]">Saving order…</div>}
          <p className="text-xs text-[var(--color-text-tertiary)]">
            Drag to reorder. This order controls how templates appear on every offer&apos;s detail page.
          </p>
        </div>
      )}
    </div>
  );
}

function CreateTemplateForm({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState("");
  const [partners, setPartners] = useState([
    { partner_label: "Partner 1", ownership_pct: "" },
    { partner_label: "Partner 2", ownership_pct: "" },
  ]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totalPct = partners.reduce((sum, p) => sum + (Number(p.ownership_pct) || 0), 0);
  const isValid = name.trim() !== "" && Math.abs(totalPct - 100) < 0.01 && partners.every((p) => p.partner_label.trim());

  const addPartner = () => {
    setPartners((prev) => [...prev, { partner_label: `Partner ${prev.length + 1}`, ownership_pct: "" }]);
  };
  const removePartner = (index: number) => {
    setPartners((prev) => prev.filter((_, i) => i !== index));
  };
  const updatePartner = (index: number, field: "partner_label" | "ownership_pct", value: string) => {
    setPartners((prev) => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  };

  const handleSave = async () => {
    if (!isValid) return;
    setSaving(true);
    setError(null);
    try {
      await createLibraryTemplate(
        name,
        partners.map((p) => ({ partner_label: p.partner_label, ownership_pct: Number(p.ownership_pct) }))
      );
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-1)] p-4">
      <label className="block text-xs text-[var(--color-text-secondary)]">
        Template name
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. 3-Way Even Split"
          className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-0)] px-2 py-1.5 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] focus:border-[var(--color-accent)]"
        />
      </label>

      <div className="mt-4 space-y-2">
        {partners.map((p, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              type="text"
              value={p.partner_label}
              onChange={(e) => updatePartner(i, "partner_label", e.target.value)}
              placeholder="Partner label"
              className="flex-1 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-0)] px-2 py-1.5 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] focus:border-[var(--color-accent)]"
            />
            <input
              type="number"
              step="any"
              value={p.ownership_pct}
              onChange={(e) => updatePartner(i, "ownership_pct", e.target.value)}
              placeholder="%"
              className="w-24 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-0)] px-2 py-1.5 text-right text-sm tabular-nums text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] focus:border-[var(--color-accent)]"
            />
            {partners.length > 1 && (
              <button
                onClick={() => removePartner(i)}
                className="text-xs"
                style={{ color: "var(--color-negative)" }}
                aria-label="Remove partner"
              >
                ✕
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center justify-between">
        <button onClick={addPartner} className="text-xs text-[var(--color-accent-strong)] hover:underline">
          + Add partner
        </button>
        <span
          className="text-xs tabular-nums"
          style={{ color: Math.abs(totalPct - 100) < 0.01 ? "var(--color-positive)" : "var(--color-negative)" }}
        >
          Total: {totalPct.toFixed(1)}% {Math.abs(totalPct - 100) < 0.01 ? "✓" : "(must equal 100%)"}
        </span>
      </div>

      {error && (
        <div
          className="mt-3 rounded-md border px-3 py-2 text-xs"
          style={{ borderColor: "var(--color-negative)", backgroundColor: "var(--color-negative-muted)", color: "var(--color-negative)" }}
        >
          {error}
        </div>
      )}

      <button
        onClick={handleSave}
        disabled={!isValid || saving}
        className="mt-4 rounded-md border px-4 py-2 text-sm font-medium disabled:opacity-50"
        style={{ borderColor: "var(--color-accent)", color: "var(--color-accent-strong)" }}
      >
        {saving ? "Creating…" : "Create Template"}
      </button>
    </div>
  );
}
